// src/pages/DashboardPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import '../Styles/DashboardPage.css';

const DashboardPage: React.FC = () => {
  const { scanType } = useParams<{ scanType?: string }>();
  const [fileName, setFileName] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<'analysis' | 'history' | 'trends'>('analysis');

  const capitalize = (str: string) => {
    if (!str) return 'Medical';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const displayType = scanType ? capitalize(scanType) : 'X-ray';

  const getDiagnosis = (type?: string) => {
    switch (type) {
      case 'tuberculosis':
        return {
          title: 'No Signs of Tuberculosis',
          findings: [
            'Lungs appear clear with no infiltrates',
            'No cavitary lesions or granulomas',
            'Normal pulmonary vasculature',
            'Intact bony structures'
          ],
          recommendations: [
            'Annual screening recommended for high-risk individuals',
            'Maintain good respiratory hygiene practices'
          ]
        };
      case 'malaria':
        return {
          title: 'No Malaria Parasites Detected',
          findings: [
            'Normal red blood cell morphology observed',
            'No ring forms or schizonts present',
            'Clear blood smear with normal cell distribution',
            'No hemozoin pigment detected'
          ],
          recommendations: [
            'Continue using mosquito protection measures',
            'Regular check-ups recommended in endemic areas'
          ]
        };
      case 'xray':
        return {
          title: 'Normal Chest X-ray',
          findings: [
            'Clear lung fields with no consolidation',
            'Normal cardiac silhouette and mediastinal contours',
            'No pleural effusion or pneumothorax',
            'Intact bony structures and diaphragmatic outlines'
          ],
          recommendations: [
            'Routine follow-up as per standard guidelines',
            'Maintain respiratory health through exercise'
          ]
        };
      case 'cancer':
        return {
          title: 'No Evidence of Malignancy',
          findings: [
            'No abnormal masses or lesions detected',
            'Normal tissue architecture preserved',
            'Regular cell distribution patterns observed',
            'No signs of metastatic involvement'
          ],
          recommendations: [
            'Continue regular screening as recommended',
            'Maintain healthy diet and lifestyle habits'
          ]
        };
      case 'fracture':
        return {
          title: 'No Fracture Detected',
          findings: [
            'Intact cortical outlines and trabecular patterns',
            'Normal joint alignment and spacing',
            'No evidence of displacement or dislocation',
            'Clear soft tissue planes with no swelling'
          ],
          recommendations: [
            'Use appropriate protective gear during activities',
            'Maintain bone health with calcium and vitamin D'
          ]
        };
      case 'brain':
        return {
          title: 'Normal Brain Scan',
          findings: [
            'No intracranial hemorrhage or mass effect',
            'Normal ventricular size and configuration',
            'Clear gray-white matter differentiation',
            'Intact calvarium with no fractures'
          ],
          recommendations: [
            'Use appropriate head protection during activities',
            'Monitor for any neurological symptoms'
          ]
        };
      default:
        return {
          title: 'Normal Thoracic Cavity',
          findings: [
            'No evidence of acute cardiopulmonary pathology',
            'Lungs are clear with no infiltrates or effusions',
            'Normal cardiac silhouette and mediastinal contours',
            'Bony thorax appears intact without fractures'
          ],
          recommendations: [
            'Continue routine follow-up as recommended',
            'Maintain healthy lifestyle habits and regular exercise'
          ]
        };
    }
  };

  const diagnosis = getDiagnosis(scanType);

  const recentUploads = [
    { name: 'Chest_Xray_001.dcm', date: '2024-07-20', status: 'Completed', type: 'X-ray' },
    { name: 'Blood_Smear_004.jpg', date: '2024-07-19', status: 'Completed', type: 'Malaria' },
    { name: 'Brain_MRI_007.dcm', date: '2024-07-18', status: 'Completed', type: 'Brain' },
    { name: 'Tissue_Sample_003.png', date: '2024-07-12', status: 'Failed', type: 'Cancer' }
  ];

  const performanceData = [
    { month: 'Feb', accuracy: 65, speed: 78, volume: 45 },
    { month: 'Mar', accuracy: 80, speed: 82, volume: 52 },
    { month: 'Apr', accuracy: 45, speed: 75, volume: 48 },
    { month: 'May', accuracy: 70, speed: 85, volume: 60 },
    { month: 'Jun', accuracy: 90, speed: 88, volume: 75 },
    { month: 'Jul', accuracy: 100, speed: 92, volume: 90 }
  ];

  const statsData = [
    { label: 'Total Analyses', value: '1,247', change: '+12%', trend: 'up' },
    { label: 'Accuracy Rate', value: '98.3%', change: '+2.4%', trend: 'up' },
    { label: 'Avg. Processing Time', value: '2.4min', change: '-0.8min', trend: 'down' },
    { label: 'Success Rate', value: '96.7%', change: '+1.2%', trend: 'up' }
  ];

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (file: File) => {
    setFileName(file.name);
    setIsProcessing(true);
    
    // Simulate processing delay
    setTimeout(() => {
      setConfidence(92 + Math.floor(Math.random() * 8));
      setIsProcessing(false);
    }, 2500);
  };

  // Determine accepted formats based on scan type
  const acceptFormats = scanType === 'malaria' ? '.jpg,.jpeg,.png,.tiff' : '.dcm,.jpg,.jpeg,.png';

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="breadcrumb">
            <Link to="/scans">Scan Types</Link>
            <span className="divider">/</span>
            <span>{displayType} Analysis</span>
          </div>
          <h1>Upload Your {displayType} Scan</h1>
          <p>Advanced AI-powered diagnostics for precise medical imaging analysis</p>
        </div>
        <div className="header-stats">
          <div className="stat-badge">
            <div className="stat-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10.5 1.5H1.5V10.5H10.5V1.5Z" />
                <path d="M22.5 1.5H13.5V10.5H22.5V1.5Z" />
                <path d="M10.极 13.5H1.5V22.5H10.5V13.5Z" />
                <path d="M22.5 13.5H13.5V22.5H22.5V13.5极" />
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">98.3%</span>
              <span className="stat-label">Accuracy</span>
            </div>
          </div>
        </div>
      </div>

      <div className="upload-section-full">
        <div 
          className="upload-area"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleFileDrop}
        >
          <div className="upload-content">
            <div className="upload-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13极4h-4v-4H7l5-5 5 5h-3z"/>
              </svg>
            </div>
            <div className="upload-text">
              <h3>Upload Your {displayType} Scan</h3>
              <p>Drag & drop your file here or browse from your device</p>
            </div>
            <label className="browse-button">
              <input 
                type="file" 
                accept={acceptFormats}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              Select File
            </label>
          </div>
          <p className="upload-subtext">Supported formats: {acceptFormats}</p>
          
          {fileName && (
            <div className="file-preview">
              <div className="file-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 2c-1.1 0-1.99.9-1.99 2L4 20c极 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"/>
                </svg>
              </div>
              <div className="file-info">
                <span className="file-name">{fileName}</span>
                <span className="file-status">
                  {isProcessing ? 'Processing...' : 'Ready for analysis'}
                </span>
              </div>
              {isProcessing && (
                <div className="processing-indicator">
                  <div className="processing-bar"></div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="stats-grid">
        {statsData.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-info">
              <span className="stat-label">{stat.label}</span>
              <span className="stat-value">{stat.value}</span>
            </div>
            <div className={`stat-trend ${stat.trend}`}>
              {stat.trend === 'up' ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 19V7.828l4.586 4.586L19 11l-7-7-7 7 1.414 1.414L11 7.828V19h2Z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 5V16.172l4.586-4.586L19 13极-7 7-7-7 1.414-1.414L11 16.172V5h2Z" />
                </svg>
              )}
              <span>{stat.change}</span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="results-section-full">
        <div className="results-tabs">
          <button 
            className={`tab ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('analysis')}
          >
            Analysis Results
          </button>
          <button 
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
          <button 
            className={`tab ${activeTab === 'trends' ? 'active' : ''}`}
            onClick={() => setActiveTab('trends')}
          >
            Trends
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'analysis' && (
            <div className="analysis-results">
              <div className="result-card">
                <div className="card-header">
                  <h2>Analysis Report</h2>
                  <span className="scan-date">{new Date().toLocaleDateString()}</span>
                </div>
                
                <div className="scan-info">
                  <span>{displayType} Scan: {fileName || 'No file uploaded'}</span>
                </div>
                
                <div className="diagnosis">
                  <h3>Diagnosis: {diagnosis.title}</h3>
                  
                  <div className="confidence-container">
                    <div className="confidence-header">
                      <span className="confidence-label">AI Confidence Level</span>
                      {confidence && <span className="confidence-value">{confidence}%</span>}
                    </div>
                    {isProcessing ? (
                      <div className="confidence-meter">
                        <div className="confidence-bar">
                          <div className="confidence-fill processing"></div>
                        </div>
                        <div className="confidence-status">Analyzing images...</div>
                      </div>
                    ) : confidence ? (
                      <div className="confidence-meter">
                        <div className="confidence-bar">
                          <div 
                            className="confidence-fill" 
                            style={{ width: `${confidence}%` }}
                          ></div>
                        </div>
                        <div className="confidence-rating">
                          {confidence >= 90 ? 'High Confidence' : 
                           confidence >= 75 ? 'Moderate Confidence' : 'Low Confidence'}
                        </div>
                      </div>
                    ) : (
                      <div className="confidence-placeholder">
                        Upload a {displayType.toLowerCase()} scan to begin analysis
                      </div>
                    )}
                  </div>
                  
                  <div className="findings-container">
                    <div className="key-findings">
                      <h4>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        Key Findings
                      </h4>
                      <ul>
                        {diagnosis.findings.map((finding, index) => (
                          <li key={index}>{finding}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="recommendations">
                      <h4>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 极 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        Recommendations
                      </h4>
                      <ul>
                        {diagnosis.recommendations.map((rec, index) => (
                          <li key={index}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="report-actions">
                    <button className="download-btn">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                      </svg>
                      Download Full Report
                    </button>
                    <button className="share-btn">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                      </svg>
                      Share with Doctor
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="history-results">
              <div className="history-card">
                <h3>Recent Analyses</h3>
                <div className="uploads-table">
                  <div className="table-header">
                    <span>File Name</span>
                    <span>Date</span>
                    <span>Type</span>
                    <span>Status</span>
                  </div>
                  <div className="table-body">
                    {recentUploads.map((upload, index) => (
                      <div key={index} className="table-row">
                        <span className="file-name">{upload.name}</span>
                        <span className="file-date">{upload.date}</span>
                        <span className="file-type">{upload.type}</span>
                        <span className={`status ${upload.status.toLowerCase()}`}>
                          <span className="status-indicator"></span>
                          {upload.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="trends-results">
              <div className="trends-card">
                <h3>Performance Analytics</h3>
                <div className="charts-container">
                  <div className="chart-container">
                    <div className="chart-header">
                      <h4>Accuracy Trend Over Time</h4>
                      <div className="chart-legend">
                        <div className="legend-item">
                          <div className="legend-color accuracy"></div>
                          <span>Accuracy</span>
                        </div>
                      </div>
                    </div>
                    <div className="chart-content">
                      <div className="classic-chart">
                        <div className="chart-y-axis">
                          <span>100%</span>
                          <span>75%</span>
                          <span>50%</span>
                          <span>25%</span>
                          <span>0%</span>
                        </div>
                        <div className="chart-main">
                          <div className="chart-grid">
                            <div className="grid-line"></div>
                            <div className="grid-line"></div>
                            <div className="grid-line"></div>
                            <div className="grid-line"></div>
                            <div className="grid-line"></div>
                          </div>
                          <div className="chart-bars">
                            {performanceData.map((data, index) => (
                              <div key={index} className="chart-bar-container">
                                <div 
                                  className="chart-bar"
                                  style={{ height: `${data.accuracy}%` }}
                                  data-value={data.accuracy}
                                >
                                  <div className="bar-value">{data.accuracy}%</div>
                                </div>
                                <div className="chart-label">{data.month}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="chart-footer">
                      <span>Monthly Performance Metrics</span>
                    </div>
                  </div>

                  <div className="metrics-grid">
                    <div className="metric">
                      <span className="metric-label">Average Accuracy</span>
                      <span className="metric-value">94.2%</span>
                      <div className="metric-trend up">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M13 19V7.828l4.586 4.586L19 11l-7-7-7 7 1.414 1.414L11 7.828V19h2Z" />
                        </svg>
                        <span>+5.3% from last month</span>
                      </div>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Processing Speed</span>
                      <span className="metric-value">2.4 min</span>
                      <div className="metric-trend down">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M13 5V16.172l4.586-4.586L19 13l-7 7-7-7 1.414-1.414L11 16.172V5h2Z" />
                        </svg>
                        <span>-0.8 min from last month</span>
                      </div>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Analyses</span>
                      <span className="metric-value">1,247</span>
                      <div className="metric-trend up">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M13 19极7.828l4.586 4.586L19 11l-7-7-7 7 1.414 1.414L11 7.828V19h2Z" />
                        </svg>
                        <span>+142 from last month</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;